import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Box, ButtonBase, Collapse, Link, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as React from "react";

export type TocItem = {
  /** 对应正文标题的 id */
  id: string;
  title: string;
  /** 标题级别：2 = h2，3 = h3 … */
  level: number;
};

type TocNode = TocItem & { number: string; children: TocNode[] };

/** 按标题级别组装成层级，并生成 1. / 2.1. 这样的编号（与原 Elementor 目录一致） */
function buildTree(items: TocItem[]): TocNode[] {
  const root: TocNode[] = [];
  const stack: TocNode[] = [];
  items.forEach((item) => {
    while (stack.length && stack[stack.length - 1].level >= item.level) stack.pop();
    const parent = stack[stack.length - 1];
    const siblings = parent ? parent.children : root;
    const node: TocNode = {
      ...item,
      number: `${parent ? parent.number : ""}${siblings.length + 1}.`,
      children: [],
    };
    siblings.push(node);
    stack.push(node);
  });
  return root;
}

function TocList({ nodes, depth = 0 }: { nodes: TocNode[]; depth?: number }): React.ReactElement {
  return (
    <Box component="ol" sx={{ listStyle: "none", m: 0, pl: depth ? 2.5 : 0 }}>
      {nodes.map((node) => (
        <Box component="li" key={node.id} sx={{ mt: 1 }}>
          <Link
            href={`#${node.id}`}
            underline="hover"
            color="text.primary"
            variant="body2"
            sx={{ display: "inline-flex", gap: 1, "&:hover": { color: "primary.main" } }}
          >
            <Box component="span" sx={{ color: "text.secondary" }}>
              {node.number}
            </Box>
            {node.title}
          </Link>
          {node.children.length > 0 && <TocList nodes={node.children} depth={depth + 1} />}
        </Box>
      ))}
    </Box>
  );
}

/**
 * 文章目录：可折叠，桌面端默认展开、平板/手机默认收起（与原页面一致）。
 * 默认状态用 CSS 断点控制，避免服务端/客户端渲染不一致。
 */
export default function PostToc({ title, items }: { title: string; items: TocItem[] }): React.ReactElement {
  const theme = useTheme();
  // null：还没点过，按断点默认；之后由用户切换
  const [open, setOpen] = React.useState<boolean | null>(null);
  const tree = React.useMemo(() => buildTree(items), [items]);
  const bodyId = React.useId();

  const handleToggle = () => {
    setOpen((prev) => {
      if (prev !== null) return !prev;
      const query = theme.breakpoints.up("md").replace(/^@media\s*/, "");
      return !window.matchMedia(query).matches;
    });
  };

  return (
    <Box
      component="nav"
      aria-label={title}
      className="not-prose"
      sx={{ my: 4, border: 1, borderColor: "divider", borderRadius: 2, overflow: "hidden" }}
    >
      <ButtonBase
        onClick={handleToggle}
        aria-controls={bodyId}
        aria-expanded={open ?? undefined}
        sx={{ width: "100%", justifyContent: "space-between", px: 3, py: 2, bgcolor: "grey.100", textAlign: "left" }}
      >
        <Typography variant="subtitle1" component="div">
          {title}
        </Typography>
        <ExpandMoreRoundedIcon
          sx={{
            ml: 2,
            transition: "transform .2s",
            transform:
              open === null ? { xs: "none", md: "rotate(180deg)" } : open ? "rotate(180deg)" : "none",
          }}
        />
      </ButtonBase>
      <Collapse in={open ?? true} id={bodyId} sx={open === null ? { display: { xs: "none", md: "block" } } : undefined}>
        <Box sx={{ px: 3, pt: 1, pb: 2.5 }}>
          <TocList nodes={tree} />
        </Box>
      </Collapse>
    </Box>
  );
}
